<template>
  <div>
    <FieldLabel v-if="showFieldHelp" :label="item.name" :help="fieldHelp" :field-id="item.id" :modal-key="modalKey" />
    <div v-for="(option, i) in listOptions" :key="option.key || i" class="mb-4">
      <div class="d-flex align-center mb-2">
        <div class="text-caption font-weight-bold mr-2">
          {{ option.group_name || "" }} / {{ option.title || "" }}
        </div>
        <v-spacer></v-spacer>
        <v-btn-toggle
          v-model="option.descr_mode"
          mandatory
          dense
          @change="onModeChange(option)"
        >
          <v-btn small value="original">Origine</v-btn>
          <v-btn small value="custom">Personnalise</v-btn>
        </v-btn-toggle>
      </div>

      <editor
        v-if="option.descr_mode === 'custom'"
        :key="`${getEditorKey(option, i)}-custom`"
        v-model="option.descr"
        :init="editorConfig"
        @onEditorChange="onDescrChange(option)"
        @EditorChange="onDescrChange(option)"
        @Onkeyup="onDescrChange(option)"
        @Onchange="onDescrChange(option)"
      />
      <div
        v-else
        class="dc-original-html"
        v-html="option.descr_original"
      ></div>
    </div>
  </div>
</template>

<script>
import mixinFields from "../../mixins/Fields";
import mixinConditionsFields from "../../mixins/conditionsFields";
import Editor from "@tinymce/tinymce-vue";

import "tinymce/tinymce.min";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/charmap";
import "tinymce/plugins/code";
import "tinymce/plugins/preview";

export default {
  name: "optionsReceptor",
  mixins: [mixinFields, mixinConditionsFields],
  components: {
    editor: Editor
  },
  data: function () {
    const origin = (typeof window !== "undefined" && window.location && window.location.origin)
      ? window.location.origin
      : "";
    const tinyBase = `${origin}/tinymce`;
    return {
      editorConfig: {
        language: "fr_FR",
        language_url: `${tinyBase}/langs/fr_FR.js`,
        height: 200,
        menubar: false,
        statusbar: false,
        resize: true,
        branding: false,
        toolbar_mode: "sliding",
        toolbar_sticky: false,
        quickbars_selection_toolbar: true,
        quickbars_insert_toolbar: false,
        toolbar: "bold italic | bullist numlist | link | code",
        extended_valid_elements: "span[class|data-dc-token|contenteditable]",
        content_style: ".dc-dyn-token{background:#eef2f6;border-radius:3px;padding:0 2px;font-weight:600;}",
        base_url: tinyBase,
        skin_url: `${tinyBase}/skins/ui/oxide`,
        content_css: `${tinyBase}/skins/content/default/content.min.css`,
        suffix: ".min"
      }
    };
  },
  computed: {
    listOptions() {
      const options = Array.isArray(this.model) ? this.model : [];
      options.forEach((option) => this.ensureOptionDescriptionState(option));
      return options;
    }
  },
  methods: {
    getEditorKey(option, index) {
      const mode = option?.descr_mode ?? "original";
      const signature = option?.descr_custom_signature ?? "";
      return `${option?.key ?? index}-${mode}-${signature}`;
    },

    getOptionQty(option) {
      if (option?.isq !== true) return 1;
      const qty = Number(option?.q);
      return Number.isFinite(qty) && qty > 0 ? qty : 1;
    },

    getUnitLabel(unitCode) {
      const map = {
        m2: "M2",
        m3: "M3",
        P: "Piece",
        S: "Service",
        ml: "M lineaire",
        l: "Litre"
      };
      return map[unitCode] ?? (unitCode ?? "");
    },

    getDynamicSignature(option) {
      return `${this.getOptionQty(option)}|${this.getUnitLabel(option?.unit ?? "")}`;
    },

    hasDynamicMarkers(html) {
      const value = String(html ?? "");
      return /data-dc-token=["'](q|unite)["']/i.test(value) || value.includes("{_q_}") || value.includes("{_unite_}");
    },

    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },

    renderCustomTemplate(sourceTemplate, option) {
      const qtyHtml = `<span class="dc-dyn-token" data-dc-token="q" contenteditable="false">${this.escapeHtml(this.getOptionQty(option))}</span>`;
      const unitHtml = `<span class="dc-dyn-token" data-dc-token="unite" contenteditable="false">${this.escapeHtml(this.getUnitLabel(option?.unit ?? ""))}</span>`;
      return String(sourceTemplate ?? "")
        .replace(/\{_q_\}/g, qtyHtml)
        .replace(/\{_unite_\}/g, unitHtml);
    },

    extractCustomTemplate(renderedHtml) {
      if (typeof document === "undefined") return String(renderedHtml ?? "");
      const wrapper = document.createElement("div");
      wrapper.innerHTML = String(renderedHtml ?? "");
      const tokenNodes = wrapper.querySelectorAll("span[data-dc-token]");
      tokenNodes.forEach((node) => {
        const tokenKey = String(node.getAttribute("data-dc-token") ?? "");
        const tokenText = tokenKey === "q" ? "{_q_}" : "{_unite_}";
        node.replaceWith(document.createTextNode(tokenText));
      });
      return wrapper.innerHTML;
    },

    syncCustomDescription(option) {
      const source = String(option?.descr_custom_source ?? option?.descr_source ?? "");
      this.$set(option, "descr", this.renderCustomTemplate(source, option));
      this.$set(option, "descr_custom_signature", this.getDynamicSignature(option));
    },

    ensureOptionDescriptionState(option) {
      if (!option || typeof option !== "object") return;

      const original = String(option.descr_original ?? option.descr ?? "");
      if (!Object.prototype.hasOwnProperty.call(option, "descr_original")) {
        this.$set(option, "descr_original", original);
      }
      if (!Object.prototype.hasOwnProperty.call(option, "descr_source")) {
        // Raw source template used for first custom init (keeps tokens).
        this.$set(option, "descr_source", String(option.descr_original ?? ""));
      }

      const mode = (option.descr_mode === "custom" || option.descr_mode === "original")
        ? option.descr_mode
        : "original";
      if (!Object.prototype.hasOwnProperty.call(option, "descr_mode") || option.descr_mode !== mode) {
        this.$set(option, "descr_mode", mode);
      }

      if (!Object.prototype.hasOwnProperty.call(option, "descr")) {
        // "descr" is visible custom html in editor.
        this.$set(option, "descr", "");
      }

      if (!Object.prototype.hasOwnProperty.call(option, "descr_custom_source")) {
        const fromVisible = this.extractCustomTemplate(String(option.descr ?? ""));
        const fallback = String(option.descr_source ?? "");
        this.$set(option, "descr_custom_source", fromVisible || fallback);
      }

      if (!Object.prototype.hasOwnProperty.call(option, "descr_custom_signature")) {
        this.$set(option, "descr_custom_signature", "");
      }

      if (option.descr_mode === "custom") {
        // Keep hidden custom source aligned with visible html.
        const visibleHtml = String(option.descr ?? "");
        const extracted = this.extractCustomTemplate(visibleHtml);
        if (extracted !== "" && extracted !== String(option.descr_custom_source ?? "")) {
          this.$set(option, "descr_custom_source", extracted);
        }

        const signature = this.getDynamicSignature(option);
        if (option.descr_custom_signature !== signature) {
          const sourceHasDynamicMarkers = this.hasDynamicMarkers(option.descr_custom_source);
          const visibleIsEmpty = String(option.descr ?? "").trim() === "";
          if (sourceHasDynamicMarkers || visibleIsEmpty) {
            this.syncCustomDescription(option);
          } else {
            this.$set(option, "descr_custom_signature", signature);
          }
        }
      }
    },

    onModeChange(option) {
      this.ensureOptionDescriptionState(option);
      if (!option || typeof option !== "object") return;

      if (option.descr_mode === "custom") {
        const visibleTemplate = this.extractCustomTemplate(String(option.descr ?? ""));
        if (String(option.descr_custom_source ?? "") === "" && visibleTemplate !== "") {
          this.$set(option, "descr_custom_source", visibleTemplate);
        }
        if (String(option.descr_custom_source ?? "") === "") {
          this.$set(option, "descr_custom_source", String(option.descr_source ?? ""));
        }

        const hasVisibleContent = String(option.descr ?? "").trim() !== "";
        const sourceHasDynamicMarkers = this.hasDynamicMarkers(option.descr_custom_source);
        if (!hasVisibleContent || sourceHasDynamicMarkers) {
          this.syncCustomDescription(option);
        } else {
          this.$set(option, "descr_custom_signature", this.getDynamicSignature(option));
        }
      }

      this.keyup(this.item, this.model);
    },

    onDescrChange(option) {
      this.ensureOptionDescriptionState(option);
      if (!option || typeof option !== "object") return;

      if (option.descr_mode !== "custom") return;
      const visibleHtml = String(option.descr ?? "");
      this.$set(option, "descr_custom_source", this.extractCustomTemplate(visibleHtml));
      this.$set(option, "descr_custom_signature", this.getDynamicSignature(option));
      this.keyup(this.item, this.model);
    }
  }
};
</script>

<style scoped>
/* Compact toolbar: keep editor usable but readable */
:deep(.tox .tox-editor-header) {
  box-shadow: none !important;
}

:deep(.tox .tox-toolbar),
:deep(.tox .tox-toolbar__primary) {
  min-height: 26px !important;
  padding: 0 !important;
}

:deep(.tox .tox-toolbar__group) {
  margin: 0 !important;
  padding: 0 2px !important;
}

:deep(.tox .tox-tbtn) {
  min-width: 26px !important;
  height: 26px !important;
  min-height: 26px !important;
  margin: 0 !important;
  padding: 0 4px !important;
}

:deep(.tox .tox-tbtn svg) {
  width: 14px !important;
  height: 14px !important;
}

.dc-original-html {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.4;
}

:deep(.dc-original-html p) {
  margin: 0 0 6px;
}

:deep(.dc-original-html p:last-child) {
  margin-bottom: 0;
}
</style>
